import classNames from 'classnames';
import { LinkProps } from 'next/link';
import {
    ButtonHTMLAttributes,
    Dispatch,
    HTMLAttributes,
    SetStateAction,
    createContext,
    forwardRef,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { useOnOutsideClickAction } from '@/shared/hooks/use-on-outside-click-action';
import { Props as ButtonProps } from '../Button/Button';
import Link from '../Link';

type DropdownTrigger = 'click' | 'hover';

const HOVER_CLOSE_DELAY = 200;

interface Props extends HTMLAttributes<HTMLElement> {
    trigger?: DropdownTrigger;
    onOpen?: () => void;
}

const DropdownContext = createContext<{
    opened: boolean;
    setOpened: Dispatch<SetStateAction<boolean>>;
    trigger: DropdownTrigger;
}>({
    opened: false,
    setOpened: () => {},
    trigger: 'click',
});

export const useDropdownContext = () => useContext(DropdownContext);

type TogglerProps = ButtonHTMLAttributes<HTMLButtonElement> &
    Partial<ButtonProps> &
    Partial<Pick<LinkProps, 'href' | 'prefetch'>> & {
        keepOpen?: boolean;
    };

const Toggler = ({ tag = 'button', href, prefetch, className, onClick, ...props }: TogglerProps) => {
    const { opened, setOpened, trigger } = useContext(DropdownContext);
    const isLink = Boolean(href);
    const Component = isLink ? Link : (tag as any);

    return (
        <Component
            {...props}
            {...(isLink ? { href, prefetch } : { type: tag === 'button' ? 'button' : undefined })}
            className={classNames('dropdown-btn', { link: isLink }, className)}
            onClick={(event: any) => {
                if (isLink || trigger === 'hover') {
                    onClick?.(event);
                    return;
                }

                setOpened(!opened);
                onClick?.(event);
            }}
            aria-haspopup="true"
        />
    );
};

const Picker = ({ tag = 'button', keepOpen, ...props }: TogglerProps) => {
    const { setOpened } = useContext(DropdownContext);
    const Component = tag as any;

    return (
        <Component
            {...props}
            type={tag === 'button' ? 'button' : undefined}
            className={classNames('dropdown-picker-btn', props.className)}
            onClick={(event: any) => {
                if (!keepOpen) setOpened(false);
                props.onClick?.(event);
            }}
        ></Component>
    );
};

const Content = forwardRef<
    HTMLDivElement,
    HTMLAttributes<HTMLElement> & {
        position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'bottom-center';
    }
>(({ children, position = 'bottom-left', ...props }, ref) => {
    return (
        <div {...props} ref={ref} className={classNames('dropdown-content', props.className, position)}>
            <div className="dropdown-content-inner">{children}</div>
        </div>
    );
});

Content.displayName = 'Content';

const Dropdown = ({ children, trigger = 'click', onOpen, onMouseEnter, onMouseLeave, ...props }: Props) => {
    const el = useRef<HTMLDivElement>(null);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const [opened, setOpened] = useState(false);
    useOnOutsideClickAction(el, () => setOpened(false));

    useEffect(() => {
        return () => clearTimeout(closeTimeoutRef.current);
    }, []);

    const clearCloseTimeout = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = undefined;
        }
    };

    return (
        <DropdownContext.Provider value={{ opened, setOpened, trigger }}>
            <div
                {...props}
                ref={el}
                onMouseEnter={(event) => {
                    if (trigger === 'hover') {
                        clearCloseTimeout();
                        setOpened(true);
                        onOpen?.();
                    }

                    onMouseEnter?.(event);
                }}
                onMouseLeave={(event) => {
                    if (trigger === 'hover') {
                        clearCloseTimeout();
                        closeTimeoutRef.current = setTimeout(() => setOpened(false), HOVER_CLOSE_DELAY);
                    }

                    onMouseLeave?.(event);
                }}
                className={classNames('dropdown js-dropdown', props.className, {
                    'dropdown--opened': opened,
                    'dropdown--hover': trigger === 'hover',
                })}
            >
                {children}
            </div>
        </DropdownContext.Provider>
    );
};

Dropdown.Toggler = Toggler;
Dropdown.Content = Content;
Dropdown.Picker = Picker;

export default Dropdown;
